import React, { useState, useEffect, useCallback } from "react";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
import "./ProjectCertifiedPayroll.scss";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { userSelector } from "../../../store/features/auth/authSlice";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Checkbox,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  ListItemText,
  TextField,
  Pagination,
  Radio,
  RadioGroup,
  styled,
  TableSortLabel,
  useTheme,
  Tooltip
} from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import DateField from "../../../Components/Inputs/DateField";
import ProFileUploadField from "../../../Components/Inputs/FileUploadField";
import { errorToast, successToast, warningToast } from "../../../../utils/toastHelper";
import usePageState from "../../../../utils/customHooks/usePageState";
import {
  GET_CERTIFIED_PAYROLL_DETAILS,
  GET_PROJECT_INFO,
  PAYROLL_SUMMARY_IMPORTS,
  TIMESHEET_IMPORT,
  CREATE_CPR_CERTIFIED_PAYROLL,
  IMPORT_SOURCES,
  UPLOAD_EMPLOYEE_PAYROLLREPORT,
  REFRESH_EXCEPTIONS
} from "../../../../utils/services/apiPath";
import { getApi, postApi } from "../../../../utils/services";
import { changeMode, convertFileToBase64, fieldTruePermission, formatPayloadDate, prepareInitialConfig } from "../../../../utils/formHelper.js";
import ProButton from "../../../Components/ProButton/index.jsx";
import ReportsLayout from "../../../Components/ProForms/ProFormsLayout/ReportsFormsLayout";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import DialogModal from "../../../Components/DialogModel/index.js";
import { jwtDecode } from "jwt-decode";
import { subModPermissionSelector } from "../../../store/features/permissions/permissionsSlice.js";
import { R_PROJECTS, R_PROJECTS_CERTIFIED_PAYROLL } from "../../../../utils/permissionReferenceConfig.js";
import usePermission from "../../../../utils/customHooks/usePermission.js";
import ProjectCertifiedPayrollTable from "./ProjectCertifiedPayrollTable.jsx";
import ProjectCertifiedPayrollSpreadsheet from "./ProjectCertifiedPayrollSpreadsheet.jsx";
import { CloudUpload } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import Loading from "../../../Components/Loading";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { useNavigate } from "react-router-dom";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { StaticDatePicker, StaticDateRangePicker, LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import Popover from "@mui/material/Popover";
import { createODataQuery } from "../../../../utils/services/oData.js";

const CertifiedPayrollConfig = {
  startDate: {
    label: "Week Ending Date",
    perKey: "WeekEndingDate",
    rules: { required: "WeekEndingDate is required!" }
  },
  uploadPDF: {
    label: "Upload PDF",
    perKey: "WeekEndingDate",
    type: "file",
    readKey: "pdfFile",
    rules: { required: "File is required!" }
  }
};

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1
});

const POPOVER_WIDTH_SINGLE_DATE = 540;
const POPOVER_WIDTH_DATE_RANGE = 850;
const DATE_PICKER_SINGLE_MIN_WIDTH = 340;
const DATE_PICKER_RANGE_MIN_WIDTH = 680;
const DATE_PICKER_SINGLE_MAX_WIDTH = 360;
const DATE_PICKER_RANGE_MAX_WIDTH = 700;
const DATE_PICKER_HEIGHT = 370;

const TIMESHEET_OPTIONS = [
  { label: "No Timesheet Data", value: "noData" },
  { label: "Timesheet Exception", value: "exception" },
  { label: "Valid Timesheet Data", value: "valid" }
];
const PAYROLL_OPTIONS = [
  { label: "No Payroll Data", value: "noData" },
  { label: "Payroll Exception", value: "exception" },
  { label: "Valid Payroll Data", value: "valid" }
];

const CPR_OPTIONS = [
  { label: "No CPR Generated", value: "noCpr" },
  { label: "Unsigned", value: "unsigned" },
  { label: "Signed", value: "signed" },
  { label: "Uploaded", value: "uploaded" }
];

//PSPM-1061 CPR ToolTip with feature flag
const InfoTooltip = () => {
  // Remove the enabled prop since we're handling it in JSX
  const tooltipContent = (
    <div>
      <div style={{ marginBottom: "8px" }}>
        <strong>Statement of Non-Performance </strong>: Statement certifying that there is no work to report for the selected time period.
      </div>
      <div style={{ marginBottom: "8px" }}>
        <strong>Amended CPR</strong>: For correcting existing employee data such as hours, rates, or fringe data; backpay/interest.
      </div>
    </div>
  );

  return (
    <Tooltip title={tooltipContent} arrow placement="right">
      <InfoOutlined
        sx={{
          fontSize: 16,
          color: "#666",
          cursor: "help",
          "&:hover": { color: "#333" }
        }}
      />
    </Tooltip>
  );
};

export const ProjectCertifiedPayroll = () => {
  const user = useSelector(userSelector);
  const { id } = useParams();
  const theme = useTheme();
  const { handleSubmit, control, setValue, reset } = useForm({
    defaultValues: {
      startDate: null
    }
  });
  const navigation = useNavigate();
  const [selectedValue, setSelectedValue] = useState("");
  const [timeSheetValue, setTimeSheetValue] = useState("");
  const [formConfig, setFormConfig] = useState(prepareInitialConfig(CertifiedPayrollConfig));
  const [projectPayrollReportData] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [weeklyReportData, setWeeklyReportData] = useState(null);
  const [currentPage1, setCurrentPage1] = useState(1);
  const [pageSize1] = useState(10);
  const [confirmation, setConfirmation] = useState(false);
  const [uploadData, setUploadData] = useState();
  const [certifiedPayrollModal, setCertifiedPayrollModal] = useState(false);
  const companyName = user?.companyName || "";
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [apiMessage, setApiMessage] = useState("");
  const [projectInfoData, setProjectInfoData] = useState();
  const [tableSortModel, setTableSortModel] = useState({ orderCol: "", orderDirection: "desc" });
  const token = localStorage.getItem("accessToken");
  const tokenData = jwtDecode(token);
  const [openErrorWarning, setOpenErrorWarning] = useState(false);
  const [errorWarningMsg, setErrorWarningMsg] = useState("");
  const [generateCprLoader, setGenerateCprLoader] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [openFinalCPRDialog, setOpenFinalCPRDialog] = useState(false);
  const [openWorkforceEnddateDialog, setOpenWorkforceEnddateDialog] = useState(false);
  const [weekEndingDate, setWeekEndingDate] = useState("");
  const [selectedRow, setSelectedRow] = useState(null);
  const [ppeDateFilter, setPpeDateFilter] = useState(null);
  const [ppeDateFilterType, setPpeDateFilterType] = useState("single");
  const [pendingPpeDateFilter, setPendingPpeDateFilter] = useState(null);
  const [pendingPpeDateFilterType, setPendingPpeDateFilterType] = useState("single");
  const [filterPopoverAnchor, setFilterPopoverAnchor] = useState(null);
  const [activeFilterColumn, setActiveFilterColumn] = useState(null);
  const [partnerFilterAnchor, setPartnerFilterAnchor] = useState(null);
  const [partnerList, setPartnerList] = useState([]);
  const [partnerFilter, setPartnerFilter] = useState([]);
  const [pendingPartnerFilter, setPendingPartnerFilter] = useState([]);
  const [partnerSearch, setPartnerSearch] = useState("");
  const [timesheetFilterAnchor, setTimesheetFilterAnchor] = useState(null);
  const [payrollFilterAnchor, setPayrollFilterAnchor] = useState(null);
  const [pendingTimesheetFilter, setPendingTimesheetFilter] = useState([]);
  const [pendingPayrollFilter, setPendingPayrollFilter] = useState([]);
  const [timesheetFilter, setTimesheetFilter] = useState([]);
  const [payrollFilter, setPayrollFilter] = useState([]);
  const [timesheetSearch, setTimesheetSearch] = useState("");
  const [payrollSearch, setPayrollSearch] = useState("");
  const [cprFilterAnchor, setCprFilterAnchor] = useState(null);
  const [pendingCprFilter, setPendingCprFilter] = useState([]);
  const [cprFilter, setCprFilter] = useState([]);
  const [cprSearch, setCprSearch] = useState("");

  // PSPM-942 Import Sources
  const includeImportSources = useFeatureFlagEnabled("import-sources");
  const [selectedImportSourceString, setSelectedImportSourceString] = useState(localStorage.getItem("importSourceString") || "ProScore Template");
  const [importSources, setImportSources] = useState([]);
  const [selectedImportSource, setSelectedImportSource] = useState(localStorage.getItem("importSource") || "1");
  // END PSPM-942

  const { "Certified Payroll List": projectCertifiedPayrollPermission } = usePermission(
    subModPermissionSelector(R_PROJECTS, R_PROJECTS_CERTIFIED_PAYROLL),
    ["Certified Payroll List"]
  );
  const certifiedPayrollLambdaImportEnabled = useFeatureFlagEnabled("certified-payroll-lambda-import-enabled");

  const projectCertifiedPayrollOptions = [
    { label: "TimeSheet", value: 1, permission: projectCertifiedPayrollPermission?.fieldPerObj?.TimeSheet?.canImport },
    { label: "Payroll Summary", value: 2, permission: projectCertifiedPayrollPermission?.fieldPerObj?.Payroll?.canImport },
    { label: "Certified Payroll", value: 3, permission: projectCertifiedPayrollPermission?.fieldPerObj?.CertifiedPayroll?.canImport }
  ];

  const handleRadioChange = value => {
    setSelectedValue(value);
  };

  const handleFileUpload = e => {
    if (e) {
      setSelectedFile(e);
    }
  };

  const handleS3UploadTimesheet = async (e, isMultiLine) => {
    if (e.target.files.length > 0) {
      const file = e.target.files[0];
      const { name } = file;
      const encoded = await convertFileToBase64(file);

      try {
        await postApi(TIMESHEET_IMPORT.replace("{projectId}", id), {
          contents: encoded,
          fileName: name,
          timesheetType: isMultiLine ? 1 : 0,
          importSourceName: selectedImportSourceString
        });
        return successToast(`Successfully uploaded ${name} for additional processing. You will be notified when it is ready to import.`);
      } catch (e) {
        return errorToast("Failed to upload file");
      }
    }
  };

  const handleS3UploadPayrollSummary = async e => {
    if (e.target.files.length > 0) {
      const file = e.target.files[0];
      const { name } = file;
      const encoded = await convertFileToBase64(file);

      try {
        await postApi(PAYROLL_SUMMARY_IMPORTS.replace("{projectId}", id), {
          contents: encoded,
          fileName: name,
          importSourceName: selectedImportSourceString
        });
        successToast(`Successfully uploaded ${name} for additional processing. You will be notified when it is ready to import.`);
      } catch (e) {
        errorToast("Failed to upload file");
      }
    }
  };

  const defaultFormProps = { control, formValues: projectPayrollReportData };

  useEffect(() => {
    setFormConfig(changeMode("edit"));
  }, []);

  const resetForm = () => {
    setFormConfig(changeMode("edit"));
    reset();
  };

  const handleSingleRowTimesheet = () => {
    setTimeSheetValue("1");
    setIsOpen(true);
  };

  const {
    currentPage,
    pageSize,
    data: dailyTimesheet,
    rowCount,
    status,
    sortModel,
    searchString,
    setPageState,
    setPagination,
    setStatus,
    setSortModel,
    setSearchString
  } = usePageState();

  const getProjectProfileData = async () => {
    const payload = {
      projectId: id
    };
    const { error, data: project } = await postApi(GET_PROJECT_INFO, payload);
    if (error) {
      // Log a user friendly error, the detailed error can live in the new relic error
      errorToast("Failed to determine upload format. Please try again or contact support if the issue persists.");
      window.newrelic?.noticeError(new Error(error), { projectId: id, context: "getProjectProfileData" });
      return;
    }
    if (!project) {
      window.newrelic?.addPageAction("timesheetImportModeUnknown", { projectId: id, reason: "GET_PROJECT_INFO returned no data" });
      return;
    }

    setProjectInfoData(project);
  };

  useEffect(() => {
    getProjectProfileData();
  }, []);

  useEffect(() => {
    const uploadFormat = projectInfoData?.uploadDailyTimeSheet;
    if (uploadFormat == null) return;
    if (uploadFormat !== 1 && uploadFormat !== 2) {
      window.newrelic?.noticeError(new Error(`Unexpected uploadDailyTimeSheet value: ${uploadFormat}`), {
        projectId: id,
        context: "renderTimesheetButtons",
        uploadDailyTimeSheet: uploadFormat
      });
    }
  }, [projectInfoData?.uploadDailyTimeSheet]);

  const onSave = async data => {
    setUploadData(data);
    setConfirmation(true);
  };

  const handleCertifiedPayrollConfirmModal = async () => {
    setConfirmation(false);
    try {
      setLoading(true);
      const base64File = await convertFileToBase64(uploadData?.uploadPDF);
      const payload = {
        file: base64File,
        fileName: selectedFile?.name,
        projectId: id,
        weekEndDate: formatPayloadDate(uploadData?.startDate)
      };
      const res = await postApi(UPLOAD_EMPLOYEE_PAYROLLREPORT, payload);
      if (res?.statusCode === 200) {
        handlePayrollTimeSheet();
        resetForm();
        setValue("startDate", null);
        setApiMessage(res.message);
      } else {
        setApiMessage(res?.error);
        handlePayrollTimeSheet();
      }
    } catch (err) {
      handlePayrollTimeSheet();
      setApiMessage("Payroll Excel Data Failed to Upload");
    } finally {
      setCertifiedPayrollModal(true);
      setLoading(false);
    }
  };

  const handleCancelModal = () => {
    setConfirmation(false);
  };

  const createSortHandler = property => () =>
    setTableSortModel(prev => ({
      orderCol: property || "",
      orderDirection: prev.orderCol === property && prev.orderDirection === "desc" ? "asc" : "desc"
    }));

  const isWithinOneWeekOrPast = dateString => {
    const unit = "day";
    const daysInWeek = 7;

    const today = dayjs().startOf(unit);
    const targetDate = dayjs(dateString).startOf(unit);

    const oneWeekBefore = targetDate.subtract(daysInWeek, unit);

    return today >= oneWeekBefore;
  };

  const handlePayrollTimeSheet = useCallback(async () => {
    if (id) {
      setLoading(true);
      const url = GET_CERTIFIED_PAYROLL_DETAILS.replace("{projectGuid}", id);

      let betweenFields = [];
      if (ppeDateFilterType === "single" && ppeDateFilter && dayjs(ppeDateFilter).isValid()) {
        const dateString = dayjs(ppeDateFilter).format("MM/DD/YYYY");
        betweenFields = [{ field: "weekEndingDate", startDate: `'${dateString}'`, endDate: `'${dateString}'` }];
      } else if (
        ppeDateFilterType === "range" &&
        Array.isArray(ppeDateFilter) &&
        ppeDateFilter[0] &&
        ppeDateFilter[1] &&
        dayjs(ppeDateFilter[0]).isValid() &&
        dayjs(ppeDateFilter[1]).isValid()
      ) {
        const startDateString = dayjs(ppeDateFilter[0]).format("MM/DD/YYYY");
        const endDateString = dayjs(ppeDateFilter[1]).format("MM/DD/YYYY");
        betweenFields = [{ field: "weekEndingDate", startDate: `'${startDateString}'`, endDate: `'${endDateString}'` }];
      } else {
        betweenFields = [];
      }

      let filterString = "";
      if (partnerFilter.length > 0) {
        filterString += `(${partnerFilter.map(pid => `clientName eq '${encodeURIComponent(pid)}'`).join(" or ")})`;
      }

      let hasTimesheetFilter = timesheetFilter.length > 0;
      let hasPayrollFilter = payrollFilter.length > 0;
      let hasCprFilter = cprFilter.length > 0;
      let fetchAll = hasTimesheetFilter || hasPayrollFilter || hasCprFilter;

      let pageSizeToUse = fetchAll ? 10000 : pageSize1;
      let pageIndexToUse = fetchAll ? 1 : currentPage1;

      const payload = {
        pageIndex: pageIndexToUse,
        pageSize: pageSizeToUse,
        searchString: "",
        orderByAscending: tableSortModel.orderDirection === "asc",
        orderCol: tableSortModel.orderCol || "date"
      };

      let { data, totalRecords, error } = await getApi(
        `${url}${createODataQuery(
          [],
          betweenFields,
          filterString,
          "",
          { orderCol: tableSortModel.orderCol || "date", orderByAscending: tableSortModel.orderDirection === "asc" },
          pageSizeToUse,
          fetchAll ? 0 : currentPage1 - 1,
          "weekEndingDate"
        )}`,
        payload
      );
      setLoading(false);
      if (error) return errorToast(error);

      setTotalRecords(totalRecords);
      data = data === null ? [] : data;
      const offset = (currentPage1 - 1) * pageSize1;

      const formattedData = data?.map((item, index) => {
        // Timesheet Status mapping
        let timesheetStatusArr = [];
        if (item.isTimesheetException) timesheetStatusArr.push("exception");
        if (item.totalHours > 0) timesheetStatusArr.push("valid");
        if (timesheetStatusArr.length === 0) timesheetStatusArr = ["noData"];

        // Payroll Status mapping
        let payrollStatusArr = [];
        if (item.isPayrollSummaryException) payrollStatusArr.push("exception");
        if (item.totalAmount > 0) payrollStatusArr.push("valid");
        if (payrollStatusArr.length === 0) payrollStatusArr = ["noData"];

        // CPR Status mapping
        let cprStatusArr = [];
        if (item.certifiedPayroll === null) {
          cprStatusArr.push("noCpr");
        }
        if (item.isCertifiedPayrollNotSigned) {
          cprStatusArr.push("unsigned");
        }
        if (item.isCertifiedPayrollSigned) {
          cprStatusArr.push("signed");
        }
        if (item.isManual) {
          cprStatusArr.push("uploaded");
        }
        // If none matched, keep as null for future
        if (cprStatusArr.length === 0) cprStatusArr = [null];

        return {
          ...item,
          id: item.weekEndingDate,
          timesheetStatus: timesheetStatusArr,
          payrollStatus: payrollStatusArr,
          cprStatus: cprStatusArr
        };
      });

      let filteredData = formattedData;

      if (ppeDateFilterType === "range" && Array.isArray(ppeDateFilter) && ppeDateFilter[0] && ppeDateFilter[1]) {
        const start = dayjs(ppeDateFilter[0]).startOf("day");
        const end = dayjs(ppeDateFilter[1]).endOf("day");
        filteredData = filteredData.filter(row => {
          let rowDate = dayjs(row.weekEndingDate, "MM/DD/YYYY", true);
          if (!rowDate.isValid()) rowDate = dayjs(row.weekEndingDate);
          if (!rowDate.isValid()) return false;
          return rowDate.isSameOrAfter(start) && rowDate.isSameOrBefore(end);
        });
      }

      if (hasTimesheetFilter || hasPayrollFilter) {
        filteredData = formattedData.filter(row => {
          const timesheetMatch = !hasTimesheetFilter || row.timesheetStatus.some(status => timesheetFilter.includes(status));
          const payrollMatch = !hasPayrollFilter || row.payrollStatus.some(status => payrollFilter.includes(status));
          return timesheetMatch && payrollMatch;
        });
      }

      if (cprFilter.length > 0) {
        filteredData = filteredData.filter(row => row.cprStatus.some(status => cprFilter.includes(status)));
      }

      const paginatedData = fetchAll ? filteredData.slice((currentPage1 - 1) * pageSize1, currentPage1 * pageSize1) : filteredData;

      const isDateFiltered =
        (ppeDateFilterType === "single" && ppeDateFilter && dayjs(ppeDateFilter).isValid()) ||
        (ppeDateFilterType === "range" && Array.isArray(ppeDateFilter) && ppeDateFilter[0] && ppeDateFilter[1]);

      setWeeklyReportData(paginatedData);
      setTotalRecords(fetchAll || isDateFiltered ? filteredData.length : totalRecords);
    }
  }, [currentPage1, tableSortModel, ppeDateFilter, ppeDateFilterType, partnerFilter, timesheetFilter, payrollFilter, cprFilter]);

  const handleRefreshAllExceptions = async () => {
    if (!weeklyReportData || weeklyReportData.length === 0) return;
    setLoading(true);
    try {
      const url = REFRESH_EXCEPTIONS.replace("{projectGuid}", id);
      // Only include weeks with exceptions
      const exceptionRows = weeklyReportData.filter(
        row => row.isTimesheetException || row.isPayrollSummaryException
      );
      const weekEndingDates = exceptionRows.map(row => row.weekEndingDate);
      if (weekEndingDates.length === 0) {
        warningToast("No exceptions found.");
        setLoading(false);
        return;
      }
      await Promise.all(
        weekEndingDates.map(weekEndingDate =>
          postApi(url, { weekEndingDate })
        )
      );
      successToast(`Exceptions for week ending ${weekEndingDates.join(", ")} refreshed.`);
      await handlePayrollTimeSheet(); // Refresh the table data
    } catch (e) {
      errorToast("Failed to refresh all exceptions.");
    } finally {
      setLoading(false);
    }
  };
  
  const handleGenerateCPR = date => {
    setWeekEndingDate(date);
    if (projectInfoData?.workforceEndDate && isWithinOneWeekOrPast(projectInfoData?.workforceEndDate)) {
      setOpenFinalCPRDialog(true);
    } else {
      createCpr(date, false, false, "CPR");
    }
  };

  const createCpr = async (date, isFinalCpr, finalCprvalue = null, reportType = null) => {
    setGenerateCprLoader(true);
    let payload = {
      projectId: id,
      weekEndingDate: date
    };
    if (isFinalCpr) {
      payload = { ...payload, isFinalCpr: finalCprvalue };
    }
    if (reportType) {
      payload = { ...payload, reportType };
    }
    try {
      const resData = await postApi(CREATE_CPR_CERTIFIED_PAYROLL, payload);
      setGenerateCprLoader(false);
      if (resData?.status) {
        handlePayrollTimeSheet();
        successToast(`CPR generated successfully`);
      } else {
        setErrorWarningMsg(resData?.message);
        setOpenErrorWarning(true);
        return;
      }
    } catch (e) {
      setGenerateCprLoader(false);
      errorToast("Failed to upload file");
    }
  };
  const handleGenerateCprWithType = (date, reportType) => {
    createCpr(date, false, null, reportType);
  };
  const handleWarningClose = () => {
    setOpenErrorWarning(false);
  };

  const handleFinalCPRCancel = () => {
    setOpenFinalCPRDialog(false);
  };

  const handleFinalCPRNo = () => {
    setOpenFinalCPRDialog(false);
    createCpr(weekEndingDate, true, false, "CPR");
  };

  const handleFinalCPRYes = () => {
    setOpenFinalCPRDialog(false);
    createCpr(weekEndingDate, true, true, "CPR");
  };

  const handleWorkforceEnddateClose = () => {
    setOpenWorkforceEnddateDialog(false);
  };

  const handleWorkforceEnddateRedirect = () => {
    setOpenWorkforceEnddateDialog(false);
    navigation(`/projects/${id}/projectinfo`);
  };

  // Filter popover open/close
  const handleFilterClose = () => {
    setFilterPopoverAnchor(null);
  };

  const clearFilterPopovers = () => {
    setFilterPopoverAnchor(null);
    setPartnerFilterAnchor(null);
    setTimesheetFilterAnchor(null);
    setPayrollFilterAnchor(null);
    setCprFilterAnchor(null);
    setActiveFilterColumn(null);
  };

  const handleClearFilters = () => {
    setCurrentPage1(1);
    setPendingPpeDateFilterType("single");
    setPendingPpeDateFilter(null);
    setPpeDateFilterType("single");
    setPpeDateFilter(null);
    setSelectedImportSource("1");
    setSelectedImportSourceString("ProScore Template");
    setPartnerFilter([]);
    setPartnerSearch("");
    setPendingPartnerFilter([]);
    setTimesheetFilter([]);
    setPendingTimesheetFilter([]);
    setTimesheetSearch("");
    setPayrollFilter([]);
    setPendingPayrollFilter([]);
    setPayrollSearch("");
    setCprFilter([]);
    setPendingCprFilter([]);
    setCprSearch("");
    clearFilterPopovers();
    setTableSortModel({
      orderCol: null,
      orderDirection: "asc"
    });
  };

  useEffect(() => {
    handlePayrollTimeSheet();
  }, [currentPage1, tableSortModel, ppeDateFilter, ppeDateFilterType, partnerFilter, timesheetFilter, payrollFilter, cprFilter]);

  const fetchPartners = async () => {
    const url = GET_CERTIFIED_PAYROLL_DETAILS.replace("{projectGuid}", id);
    const { data } = await getApi(`${url}?$top=1000&$skip=0&$count=true`);
    const uniquePartners = [...new Set((data || []).map(item => item.clientName).filter(Boolean))].map(name => ({ label: name, value: name }));
    setPartnerList(uniquePartners);
  };

  useEffect(() => {
    fetchPartners();
  }, [id]);

  const handleSingleRowTimesheetDownload = () => {
    const filePath = "/static/Images/Excel/TimesheetImportTemplate-SingleRow.xlsx";
    window.open(filePath, "_blank");
  };

  const handleMultiRowTimesheetDownload = () => {
    setTimeSheetValue("2");
    const isVersionTwo = projectInfoData?.cprVersionLabel?.includes("1/31/2028");
    const filePath = isVersionTwo
      ? "/static/Images/Excel/Timesheet_Template_Version_June2026.xlsx"
      : "/static/Images/Excel/TimesheetImportTemplate-MultiRow.xlsx";
    window.open(filePath, "_blank");
  };

  const handleDownloadPayroll = () => {
    const isVersionTwo = projectInfoData?.cprVersionLabel?.includes("1/31/2028");
    const filePath = isVersionTwo ? "/static/Images/Excel/Payroll_Summary_Template_Version_Aug2026.xlsx" : "/static/Images/Excel/PayrollSummaryTemplateV1.xlsx";

    window.open(filePath, "_blank");
  };

  const handleChangePage = (event, newPage) => {
    setCurrentPage1(newPage);
  };

  // PSPM-942: add Import Source Select Input
  // state variables are up in state variables list

  const setImportSourceSelectInputValue = function () {
    const value = localStorage.getItem("importSource");
    setSelectedImportSource(value || "1");
  };

  const fetchImportSources = async () => {
    const impSources = await getApi(IMPORT_SOURCES);
    setImportSources(
      impSources.map(item => ({
        label: item.importSourceName,
        value: item.importSourceId
      }))
    );
  };

  useEffect(() => {
    if (includeImportSources) {
      fetchImportSources();
      setImportSourceSelectInputValue();
    }
  }, []);

  // remember the selected value for them if they interact with the dropdown
  const handleImportSourcesChange = event => {
    const value = event.target.value;
    const selectedLabel = importSources.find(source => source.value === value)?.label || "ProScore Template";
    setSelectedImportSource(value);
    setSelectedImportSourceString(selectedLabel);
    localStorage.setItem("importSource", value);
    localStorage.setItem("importSourceString", selectedLabel);
  };

  const renderImportSourceSelect = (value, onChange, label = "Import Source") => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      <FormControl variant="filled">
        <InputLabel id="import-source-select-label">{label}</InputLabel>
        <Select labelId="import-source-select-label" value={value} label={label} onChange={onChange} variant="filled" displayEmpty>
          <MenuItem value="0" disabled>
            Select Import Source
          </MenuItem>
          {importSources.length > 0 &&
            importSources.map(importSource => (
              <MenuItem key={importSource.value} value={importSource.value}>
                {importSource.label}
              </MenuItem>
            ))}
        </Select>
      </FormControl>
    </Box>
  );

  useEffect(() => {
    if (selectedImportSourceString === "ProScore Template" && selectedValue === "1") {
      setTimeSheetValue("3");
    } else if (selectedImportSourceString === "ProScore Template" && selectedValue === "2") {
      setTimeSheetValue("2");
    }
  }, [selectedImportSourceString, selectedValue]);

  const handleTimesheetImportButtonClick = () => {
    setTimeSheetValue("3");
    if (selectedImportSourceString === "ProScore Template") {
      setIsOpen(true);
    } else {
      const fileInput = document.createElement("input");
      fileInput.type = "file";
      fileInput.accept = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      fileInput.onchange = event => handleS3UploadTimesheet(event, true);
      fileInput.click();
    }
  };

  const renderTimesheetButtons = () => {
    const uploadFormat = projectInfoData?.uploadDailyTimeSheet;

    if (uploadFormat == null) {
      return null;
    }

    if (uploadFormat === 2) {
      return (
        <>
          <ProButton className="primary pro_button" sx={{ minWidth: 180 }} onClick={handleTimesheetImportButtonClick}>
            Upload Weekly Timesheet
          </ProButton>
          <ProButton className="primary pro_button" sx={{ minWidth: 180 }} onClick={handleMultiRowTimesheetDownload}>
            Download Timesheet Template
          </ProButton>
        </>
      );
    }

    if (uploadFormat === 1) {
      return (
        <>
          {certifiedPayrollLambdaImportEnabled && (
            <Button component="label" role={undefined} variant="contained" tabIndex={-1} startIcon={<CloudUpload />} sx={{ minWidth: 180 }}>
              Upload files
              <VisuallyHiddenInput
                type="file"
                onChange={event => handleS3UploadTimesheet(event, false)}
                accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              />
            </Button>
          )}
          <ProButton className="primary pro_button1" sx={{ minWidth: 180 }} onClick={handleSingleRowTimesheet}>
            Import TimeSheet
          </ProButton>
          <ProButton className="primary pro_button" sx={{ minWidth: 180 }} onClick={handleSingleRowTimesheetDownload}>
            Timesheet Import Template
          </ProButton>
        </>
      );
    }

    return null;
  };

  const handlePayrollSummaryImportButtonClick = () => {
    if (selectedImportSourceString === "ProScore Template") {
      setIsOpen(true);
    } else {
      const fileInput = document.createElement("input");
      fileInput.type = "file";
      fileInput.accept = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      fileInput.onchange = event => handleS3UploadPayrollSummary(event);
      fileInput.click();
    }
  };

  const applyPartnerFilter = newFilter => {
    setPartnerFilter(newFilter);
    setCurrentPage1(1);
  };

  const isPpeDateFilterActive = () =>
    (ppeDateFilterType === "single" && ppeDateFilter) ||
    (ppeDateFilterType === "range" && Array.isArray(ppeDateFilter) && ppeDateFilter[0] && ppeDateFilter[1]);

  // Helper to check if any filter is active
  const anyFilterActive =
    isPpeDateFilterActive() || partnerFilter.length > 0 || timesheetFilter.length > 0 || payrollFilter.length > 0 || cprFilter.length > 0;

  return (
    <ReportsLayout>
      {(generateCprLoader || exportLoading) && <Loading />}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "flex-end", gap: 2 }}>
          <FormControl component="fieldset" className="proCheckbox">
            <FormLabel component="legend" sx={{ color: theme.palette.projects.certifiedpayroll.formLabelColor }}>
              Select the option to import
            </FormLabel>
            <RadioGroup value={selectedValue} onChange={e => handleRadioChange(e.target.value)}>
              {projectCertifiedPayrollOptions
                ?.filter(option => option.permission)
                ?.map(option => (
                  <FormControlLabel
                    sx={{ color: theme.palette.projects.certifiedpayroll.optionsColor }}
                    key={option.value}
                    value={String(option.value)}
                    control={<Radio />}
                    label={option.label}
                  />
                ))}
            </RadioGroup>
          </FormControl>
        </Box>
        <Box sx={{ display: "flex", alignItems: "flex-end", gap: 2 }}>
          <Button
            variant="outlined"
            size="small"
            sx={{
              whiteSpace: "nowrap",
              minWidth: 160,
              height: 40
            }}
            onClick={handleRefreshAllExceptions}
          >
            Refresh Exceptions
          </Button>
          <Button
            variant="outlined"
            size="small"
            sx={{
              whiteSpace: "nowrap",
              minWidth: 120,
              height: 40,
              "& .MuiButton-endIcon": {
                marginLeft: "2px",
                marginRight: 0
              }
            }}
            onClick={handleClearFilters}
            endIcon={anyFilterActive ? <FilterAltIcon /> : <FilterAltOffIcon />}
          >
            Clear Filters
          </Button>

          <ProjectCertifiedPayrollSpreadsheet
            id={id}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            timeSheetValue={timeSheetValue}
            setTimeSheetValue={setTimeSheetValue}
            selectedValue={selectedValue}
            setSelectedValue={setSelectedValue}
            apiMessage={apiMessage}
            setApiMessage={setApiMessage}
            certifiedPayrollModal={certifiedPayrollModal}
            setCertifiedPayrollModal={setCertifiedPayrollModal}
            handlePayrollTimeSheet={handlePayrollTimeSheet}
            projectInfo={projectInfoData}
          />

          {selectedValue === "1" && (
            <>
              {renderTimesheetButtons()}
              {includeImportSources && renderImportSourceSelect(selectedImportSource, handleImportSourcesChange)}
            </>
          )}
          {selectedValue === "2" && (
            <>
              <ProButton className="primary pro_button1" sx={{ minWidth: 180 }} onClick={handlePayrollSummaryImportButtonClick}>
                Upload Payroll Summary
              </ProButton>
              <ProButton className="primary pro_button" sx={{ minWidth: 180 }} onClick={handleDownloadPayroll}>
                Download Payroll Summary Template
              </ProButton>
              {includeImportSources && renderImportSourceSelect(selectedImportSource, handleImportSourcesChange)}
            </>
          )}
          {selectedValue === "3" && (
            <div>
              <Box sx={{ width: "100%", mt: 2, display: "flex", justifyContent: "flex-end" }}>
                <Box sx={{ display: "flex", alignItems: "flex-end", gap: 2 }}>
                  <DateField {...defaultFormProps} {...formConfig.startDate} permission={fieldTruePermission} />
                  <ProFileUploadField
                    {...defaultFormProps}
                    {...formConfig.uploadPDF}
                    inputProps={{ accept: ".pdf" }}
                    fieldProps={{
                      onChange: e => {
                        setValue("uploadPDF", e);
                        handleFileUpload(e);
                      }
                    }}
                    permission={fieldTruePermission}
                  />
                  <ProButton onClick={handleSubmit(onSave)}>Save</ProButton>
                  <DialogModal
                    show={confirmation}
                    title="Confirm"
                    message={`Please acknowledge that by uploading a Certified Payroll
                      report, you are acknowledging that ProScore did not generate the Certified Payroll Report for
                      this pay period and that ProScore is not responsible for the accuracy and completeness of the
                      contents of the uploaded Certified Payroll Report. As such, any audits, inquiries, or exception
                      filing for this Certified Payroll Report will be directed back to ${tokenData?.GlobalClientName}`}
                    onConfirm={handleCertifiedPayrollConfirmModal}
                    onCancel={handleCancelModal}
                  />
                </Box>
              </Box>
            </div>
          )}
        </Box>
      </Box>

      <Box sx={{ pb: 2, maxWidth: "1440px", margin: "1rem auto" }}>
        {projectCertifiedPayrollPermission?.canView && (
          <div className="certified-payroll">
            <TableContainer component={Paper}>
              <Table aria-label="collapsible table">
                <TableHead className="certified-payroll_table">
                  <TableRow>
                    <TableCell>
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <TableSortLabel
                          direction={tableSortModel.orderDirection}
                          active={tableSortModel?.orderCol === "date"}
                          onClick={createSortHandler("date")}
                        >
                          PPE Date
                        </TableSortLabel>
                        {/* Show filter icon if PPE Date filter is active */}
                        {isPpeDateFilterActive() ? <FilterAltIcon color="primary" fontSize="small" /> : null}
                        <IconButton
                          size="small"
                          onClick={e => {
                            setFilterPopoverAnchor(e.currentTarget);
                            setActiveFilterColumn("ppeDate");
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <TableSortLabel
                          direction={tableSortModel.orderDirection}
                          active={tableSortModel?.orderCol === "clientName"}
                          onClick={createSortHandler("clientName")}
                        >
                          Partner Name
                        </TableSortLabel>
                        {partnerFilter.length > 0 && <FilterAltIcon color="primary" fontSize="small" />}
                        <IconButton
                          size="small"
                          onClick={e => {
                            setPartnerFilterAnchor(e.currentTarget);
                            setActiveFilterColumn("partnerName");
                            setPendingPartnerFilter(partnerFilter);
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </div>
                    </TableCell>
                    {projectCertifiedPayrollPermission?.fieldPerObj?.TimeSheet?.canView && (
                      <TableCell align="left">
                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          <TableSortLabel
                            direction={tableSortModel.orderDirection}
                            active={tableSortModel?.orderCol === "totalHours"}
                            onClick={createSortHandler("totalHours")}
                          >
                            Timesheet
                          </TableSortLabel>
                          {timesheetFilter.length > 0 && <FilterAltIcon color="primary" fontSize="small" />}
                          <IconButton
                            size="small"
                            onClick={e => {
                              setTimesheetFilterAnchor(e.currentTarget);
                              setActiveFilterColumn("timesheet");
                              setPendingTimesheetFilter(timesheetFilter);
                            }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </div>
                      </TableCell>
                    )}
                    {projectCertifiedPayrollPermission?.fieldPerObj?.Payroll?.canView && (
                      <>
                        <TableCell align="left">
                          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            <TableSortLabel
                              direction={tableSortModel.orderDirection}
                              active={tableSortModel?.orderCol === "totalAmount"}
                              onClick={createSortHandler("totalAmount")}
                            >
                              Payroll Summary
                            </TableSortLabel>
                            {payrollFilter.length > 0 && <FilterAltIcon color="primary" fontSize="small" />}
                            <IconButton
                              size="small"
                              onClick={e => {
                                setPayrollFilterAnchor(e.currentTarget);
                                setActiveFilterColumn("payroll");
                                setPendingPayrollFilter(payrollFilter);
                              }}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </div>
                        </TableCell>

                        <TableCell align="center">
                          <TableSortLabel
                            direction={tableSortModel.orderDirection}
                            active={tableSortModel?.orderCol === "payrollNumber"}
                            onClick={createSortHandler("payrollNumber")}
                          >
                            Payroll Number
                          </TableSortLabel>
                        </TableCell>
                      </>
                    )}
                    {projectCertifiedPayrollPermission?.fieldPerObj?.CertifiedPayroll?.canView && (
                      <TableCell align="left">
                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          <TableSortLabel
                            direction={tableSortModel.orderDirection}
                            active={tableSortModel?.orderCol === "certifiedPayroll"}
                            onClick={createSortHandler("certifiedPayroll")}
                          >
                            Certified Payroll (WH-347)
                          </TableSortLabel>
                          {cprFilter.length > 0 && <FilterAltIcon color="primary" fontSize="small" />}
                          <IconButton
                            size="small"
                            onClick={e => {
                              setCprFilterAnchor(e.currentTarget);
                              setActiveFilterColumn("cpr");
                              setPendingCprFilter(cprFilter);
                            }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </div>
                      </TableCell>
                    )}

                    <TableCell>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <TableSortLabel
                          direction={tableSortModel.orderDirection}
                          active={tableSortModel?.orderCol === "clientName"}
                          onClick={createSortHandler("clientName")}
                        >
                          Action
                        </TableSortLabel>
                        <InfoTooltip />
                      </div>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <ProjectCertifiedPayrollTable
                    id={id}
                    loading={loading}
                    companyName={companyName}
                    weeklyReportData={weeklyReportData}
                    projectCertifiedPayrollPermission={projectCertifiedPayrollPermission}
                    dailyTimesheet={dailyTimesheet}
                    status={status}
                    setStatus={setStatus}
                    pageSize={pageSize}
                    setPagination={setPagination}
                    searchString={searchString}
                    setSearchString={setSearchString}
                    sortModel={sortModel}
                    setSortModel={setSortModel}
                    setPageState={setPageState}
                    rowCount={rowCount}
                    currentPage={currentPage}
                    tokenData={tokenData}
                    handleGenerateCPR={handleGenerateCPR}
                    handlePayrollTimeSheet={handlePayrollTimeSheet}
                    handleGenerateCprWithType={handleGenerateCprWithType}
                    selectedRow={selectedRow}
                    setSelectedRow={setSelectedRow}
                    setExportLoading={setExportLoading}
                  />
                </TableBody>
              </Table>
            </TableContainer>
            <div style={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
              <Pagination
                count={Math.ceil((totalRecords || 0) / pageSize1)}
                page={currentPage1}
                onChange={handleChangePage}
                color="primary"
                size="large"
              />
            </div>
          </div>
        )}
      </Box>

      {/* Filter Popover for PPE Date */}
      <Popover
        open={Boolean(filterPopoverAnchor) && activeFilterColumn === "ppeDate"}
        anchorEl={filterPopoverAnchor}
        onClose={handleFilterClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{
          sx: {
            width: pendingPpeDateFilterType === "single" ? POPOVER_WIDTH_SINGLE_DATE : POPOVER_WIDTH_DATE_RANGE,
            maxWidth: "99vw",
            overflow: "visible",
            display: "flex",
            flexDirection: "row",
            p: 0
          }
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", minWidth: 120, p: 2 }}>
          <Box>
            <Box
              onClick={() => {
                setPendingPpeDateFilterType("single");
                setPendingPpeDateFilter(null);
              }}
              sx={{
                cursor: "pointer",
                px: 2,
                py: 1,
                color: pendingPpeDateFilterType === "single" ? "primary.main" : "text.primary",
                fontWeight: pendingPpeDateFilterType === "single" ? "bold" : "normal",
                backgroundColor: pendingPpeDateFilterType === "single" ? "action.selected" : "transparent",
                borderRadius: 1,
                transition: "background 0.2s",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "clip"
              }}
            >
              Single Date
            </Box>
            <Divider flexItem />
            <Box
              onClick={() => {
                setPendingPpeDateFilterType("range");
                setPendingPpeDateFilter([null, null]);
              }}
              sx={{
                cursor: "pointer",
                px: 2,
                py: 1,
                color: pendingPpeDateFilterType === "range" ? "primary.main" : "text.primary",
                fontWeight: pendingPpeDateFilterType === "range" ? "bold" : "normal",
                backgroundColor: pendingPpeDateFilterType === "range" ? "action.selected" : "transparent",
                borderRadius: 1,
                transition: "background 0.2s",
                whiteSpace: "nowrap", // Prevents wrapping
                overflow: "hidden", // Hides overflow
                textOverflow: "clip"
              }}
            >
              Date Range
            </Box>
          </Box>
        </Box>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <Box
          sx={{
            flex: 1,
            minWidth: pendingPpeDateFilterType === "single" ? DATE_PICKER_SINGLE_MIN_WIDTH : DATE_PICKER_RANGE_MIN_WIDTH,
            maxWidth: pendingPpeDateFilterType === "single" ? DATE_PICKER_SINGLE_MAX_WIDTH : DATE_PICKER_RANGE_MAX_WIDTH,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            justifyContent: "flex-start",
            p: 2,
            pt: 1,
            pb: 1
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box
              sx={{
                width: pendingPpeDateFilterType === "single" ? DATE_PICKER_SINGLE_MIN_WIDTH : DATE_PICKER_RANGE_MIN_WIDTH,
                height: DATE_PICKER_HEIGHT,
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "flex-start"
              }}
            >
              {pendingPpeDateFilterType === "single" ? (
                <StaticDatePicker
                  displayStaticWrapperAs="desktop"
                  value={pendingPpeDateFilter}
                  onChange={date => setPendingPpeDateFilter(date)}
                  sx={{ width: DATE_PICKER_SINGLE_MIN_WIDTH, height: DATE_PICKER_HEIGHT }}
                />
              ) : (
                <StaticDateRangePicker
                  displayStaticWrapperAs="desktop"
                  value={pendingPpeDateFilter}
                  onChange={range => setPendingPpeDateFilter(range)}
                  sx={{ width: DATE_PICKER_RANGE_MIN_WIDTH, height: DATE_PICKER_HEIGHT }}
                />
              )}
            </Box>
            <Box sx={{ display: "flex", gap: 0, mt: 2, width: "100%" }}>
              <Button
                size="medium"
                variant="contained"
                sx={{ flex: 1, borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                onClick={() => {
                  setPpeDateFilter(pendingPpeDateFilter);
                  setPpeDateFilterType(pendingPpeDateFilterType);
                  setCurrentPage1(1);
                  handleFilterClose();
                }}
                disabled={
                  (pendingPpeDateFilterType === "single" && !pendingPpeDateFilter) ||
                  (pendingPpeDateFilterType === "range" &&
                    (!Array.isArray(pendingPpeDateFilter) ||
                      pendingPpeDateFilter.length !== 2 ||
                      !pendingPpeDateFilter[0] ||
                      !pendingPpeDateFilter[1]))
                }
              >
                Apply
              </Button>
              <Button
                size="medium"
                sx={{ flex: 1, borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                onClick={() => {
                  setPendingPpeDateFilter(null);
                  setPendingPpeDateFilterType("single");
                  setPpeDateFilter(null);
                  setPpeDateFilterType("single");
                  setCurrentPage1(1);
                  handleFilterClose();
                }}
              >
                Reset
              </Button>
            </Box>
          </LocalizationProvider>
        </Box>
      </Popover>

      {/* Partner Name Filter Popover */}
      <Popover
        open={Boolean(partnerFilterAnchor) && activeFilterColumn === "partnerName"}
        anchorEl={partnerFilterAnchor}
        onClose={() => setPartnerFilterAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{ sx: { width: 320, p: 2 } }}
      >
        <Box sx={{ mb: 1 }}>
          <Box sx={{ fontWeight: 400, mb: 1 }}>Filter Partner</Box>
          <TextField
            size="small"
            placeholder="Search partner"
            value={partnerSearch}
            onChange={e => setPartnerSearch(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: "action.active", mr: 1 }} />
            }}
          />
        </Box>
        <Box sx={{ maxHeight: 240, overflowY: "auto" }}>
          {partnerList
            .filter(p => p.label?.toLowerCase().includes(partnerSearch.toLowerCase()))
            .map(partner => (
              <MenuItem
                key={partner.value}
                dense
                onClick={() => {
                  setPendingPartnerFilter(prev =>
                    prev.includes(partner.value) ? prev.filter(id => id !== partner.value) : [...prev, partner.value]
                  );
                }}
              >
                <Checkbox checked={pendingPartnerFilter.includes(partner.value)} />
                <ListItemText primary={partner.label} />
              </MenuItem>
            ))}
        </Box>
        <Box sx={{ display: "flex", gap: 0, mt: 2, width: "100%" }}>
          <Button
            size="medium"
            variant="contained"
            sx={{ flex: 1, borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
            onClick={() => {
              applyPartnerFilter(pendingPartnerFilter);
              setPartnerFilterAnchor(null);
            }}
            disabled={pendingPartnerFilter.length === 0}
          >
            Apply
          </Button>
          <Button
            size="medium"
            sx={{ flex: 1, borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
            onClick={() => {
              setPendingPartnerFilter([]);
              setPartnerSearch("");
              applyPartnerFilter([]);
              setPartnerFilterAnchor(null);
            }}
          >
            Reset
          </Button>
        </Box>
      </Popover>

      {/* Timesheet Filter Popover */}
      <Popover
        open={Boolean(timesheetFilterAnchor) && activeFilterColumn === "timesheet"}
        anchorEl={timesheetFilterAnchor}
        onClose={() => setTimesheetFilterAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{ sx: { width: 320, p: 2 } }}
      >
        <Box sx={{ mb: 1 }}>
          <Box sx={{ fontWeight: 400, mb: 1 }}>Filter Timesheet Status</Box>
          <TextField
            size="small"
            placeholder="Search"
            value={timesheetSearch}
            onChange={e => setTimesheetSearch(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: "action.active", mr: 1 }} />
            }}
          />
        </Box>
        <Box sx={{ maxHeight: 240, overflowY: "auto" }}>
          {TIMESHEET_OPTIONS.filter(opt => opt.label.toLowerCase().includes(timesheetSearch.toLowerCase())).map(opt => (
            <MenuItem
              key={opt.value}
              dense
              onClick={() => {
                setPendingTimesheetFilter(prev => (prev.includes(opt.value) ? prev.filter(v => v !== opt.value) : [...prev, opt.value]));
              }}
            >
              <Checkbox checked={pendingTimesheetFilter.includes(opt.value)} />
              <ListItemText primary={opt.label} />
            </MenuItem>
          ))}
        </Box>
        <Box sx={{ display: "flex", gap: 0, mt: 2, width: "100%" }}>
          <Button
            size="medium"
            variant="contained"
            sx={{ flex: 1, borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
            onClick={() => {
              setTimesheetFilter(pendingTimesheetFilter);
              setCurrentPage1(1);
              setTimesheetFilterAnchor(null);
            }}
            disabled={pendingTimesheetFilter.length === 0}
          >
            Apply
          </Button>
          <Button
            size="medium"
            sx={{ flex: 1, borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
            onClick={() => {
              setPendingTimesheetFilter([]);
              setTimesheetFilter([]);
              setTimesheetSearch("");
              setCurrentPage1(1);
              setTimesheetFilterAnchor(null);
            }}
          >
            Reset
          </Button>
        </Box>
      </Popover>

      {/* Payroll Summary Filter Popover */}
      <Popover
        open={Boolean(payrollFilterAnchor) && activeFilterColumn === "payroll"}
        anchorEl={payrollFilterAnchor}
        onClose={() => setPayrollFilterAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{ sx: { width: 320, p: 2 } }}
      >
        <Box sx={{ mb: 1 }}>
          <Box sx={{ fontWeight: 400, mb: 1 }}>Filter Payroll Status</Box>
          <TextField
            size="small"
            placeholder="Search"
            value={payrollSearch}
            onChange={e => setPayrollSearch(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: "action.active", mr: 1 }} />
            }}
          />
        </Box>
        <Box sx={{ maxHeight: 240, overflowY: "auto" }}>
          {PAYROLL_OPTIONS.filter(opt => opt.label.toLowerCase().includes(payrollSearch.toLowerCase())).map(opt => (
            <MenuItem
              key={opt.value}
              dense
              onClick={() => {
                setPendingPayrollFilter(prev => (prev.includes(opt.value) ? prev.filter(v => v !== opt.value) : [...prev, opt.value]));
              }}
            >
              <Checkbox checked={pendingPayrollFilter.includes(opt.value)} />
              <ListItemText primary={opt.label} />
            </MenuItem>
          ))}
        </Box>
        <Box sx={{ display: "flex", gap: 0, mt: 2, width: "100%" }}>
          <Button
            size="medium"
            variant="contained"
            sx={{ flex: 1, borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
            onClick={() => {
              setPayrollFilter(pendingPayrollFilter);
              setCurrentPage1(1);
              setPayrollFilterAnchor(null);
            }}
            disabled={pendingPayrollFilter.length === 0}
          >
            Apply
          </Button>
          <Button
            size="medium"
            sx={{ flex: 1, borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
            onClick={() => {
              setPendingPayrollFilter([]);
              setPayrollFilter([]);
              setPayrollSearch("");
              setCurrentPage1(1);
              setPayrollFilterAnchor(null);
            }}
          >
            Reset
          </Button>
        </Box>
      </Popover>

      {/* CPR Filter Popover */}
      <Popover
        open={Boolean(cprFilterAnchor) && activeFilterColumn === "cpr"}
        anchorEl={cprFilterAnchor}
        onClose={() => setCprFilterAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{ sx: { width: 320, p: 2 } }}
      >
        <Box sx={{ mb: 1 }}>
          <Box sx={{ fontWeight: 400, mb: 1 }}>Filter CPR Status</Box>
          <TextField
            size="small"
            placeholder="Search"
            value={cprSearch}
            onChange={e => setCprSearch(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: "action.active", mr: 1 }} />
            }}
          />
        </Box>
        <Box sx={{ maxHeight: 240, overflowY: "auto" }}>
          {CPR_OPTIONS.filter(opt => opt.label.toLowerCase().includes(cprSearch.toLowerCase())).map(opt => (
            <MenuItem
              key={opt.value}
              dense
              onClick={() => {
                setPendingCprFilter(prev => (prev.includes(opt.value) ? prev.filter(v => v !== opt.value) : [...prev, opt.value]));
              }}
            >
              <Checkbox checked={pendingCprFilter.includes(opt.value)} />
              <ListItemText primary={opt.label} />
            </MenuItem>
          ))}
        </Box>
        <Box sx={{ display: "flex", gap: 0, mt: 2, width: "100%" }}>
          <Button
            size="medium"
            variant="contained"
            sx={{ flex: 1, borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
            onClick={() => {
              setCprFilter(pendingCprFilter);
              setCurrentPage1(1);
              setCprFilterAnchor(null);
            }}
            disabled={pendingCprFilter.length === 0}
          >
            Apply
          </Button>
          <Button
            size="medium"
            sx={{ flex: 1, borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
            onClick={() => {
              setPendingCprFilter([]);
              setCprFilter([]);
              setCprSearch("");
              setCurrentPage1(1);
              setCprFilterAnchor(null);
            }}
          >
            Reset
          </Button>
        </Box>
      </Popover>

      <Dialog open={openErrorWarning} onClose={handleWarningClose} sx={{ "& .MuiPaper-root": { borderRadius: 3 } }}>
        <DialogTitle>Message</DialogTitle>
        <DialogContent sx={{ textAlign: "center", p: 3 }}>
          <DialogContentText>{errorWarningMsg}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <ProButton onClick={handleWarningClose}>Okay</ProButton>
        </DialogActions>
      </Dialog>

      <Dialog open={openFinalCPRDialog} onClose={handleFinalCPRCancel} sx={{ "& .MuiPaper-root": { borderRadius: 3 } }}>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            position: "relative",
            p: 5
          }}
        >
          <DialogContentText sx={{ textAlign: "center" }}>{`Is this the Final CPR for this Project?`}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <ProButton variant="outlined" onClick={handleFinalCPRCancel}>
            Cancel
          </ProButton>
          <ProButton variant="contained" onClick={handleFinalCPRNo}>
            No
          </ProButton>
          <ProButton variant="contained" onClick={handleFinalCPRYes}>
            Yes
          </ProButton>
        </DialogActions>
      </Dialog>

      <Dialog open={openWorkforceEnddateDialog} onClose={handleWorkforceEnddateClose} sx={{ "& .MuiPaper-root": { borderRadius: 3 } }}>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            position: "relative",
            p: 5
          }}
        >
          <DialogContentText sx={{ textAlign: "center" }}>{`Please set a Workforce End Date in My Project Info.`}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <ProButton variant="outlined" onClick={handleWorkforceEnddateClose}>
            Cancel
          </ProButton>
          <ProButton variant="contained" onClick={handleWorkforceEnddateRedirect}>
            Go to My Project Info
          </ProButton>
        </DialogActions>
      </Dialog>
    </ReportsLayout>
  );
};
